import { type SharedClientBoardSource as SharedClientBoardSourceType } from '@gorilla/portal-shared/src/schemas';
import useSWRMutation from 'swr/mutation';
import { Completed, Item, MoveArrowRight, Person, ConvertToSubitem, AddSmall, LearnMore, Warning } from '@vibe/icons';
import { Box, Flex, Icon, Text, Divider, IconButton, Tooltip } from '@vibe/core';
import { enqueueSnackbar } from 'notistack';
import { type SharedClientBoardSource } from '@gorilla/portal-shared/src/schemas';
import { ColumnPicker } from '../../shared/components/ColumnPicker';
import { monday } from '../../services/monday';
import { type MondayContext } from '../../hooks/use-portal';
import { useSingleBoard } from '../../shared/hooks/use-boards';
import {
  extractBoardNameAndErrorMessage,
  extractConnectBoardColumnErrorMessage,
  BoardSourceExplainer,
  EditBoardField,
  EditBoardSourceNameField,
} from './common';
import { createConnectBoardColumn, type Board as BoardType } from '@gorilla/common/src/lib/monday-api/api';
import { createMondayClient } from '@gorilla/common/src/lib/monday-api/common';
import { createMondaySeamlessRequester } from '@gorilla/common/src/lib/monday-api/seamless';
import { extractErrorMessage } from '../../lib/utils';
import { ExternalLink } from '../ExternalLink';

type ConnectBoardColumnFieldProps = {
  board?: BoardType | null;
  headline: string;
  introText: string;
  helpText: string;
  errorMessage?: string;
  value?: string;
  isCompleted: boolean;
  onChange: (columnId: string) => void;
};

function ConnectBoardColumnField({
  board,
  headline,
  introText,
  helpText,
  errorMessage,
  value,
  isCompleted,
  onChange,
}: ConnectBoardColumnFieldProps) {
  const {
    trigger: triggerAddConnectBoardColumn,
    isMutating: isAddingConnectBoardColumn,
    error: addConnectBoardColumnError,
  } = useSWRMutation('/add-connect-board-column', async (_key, { arg: targetBoardId }: { arg: number }) => {
    try {
      const mContext = ((await monday.get('context')) as any).data as MondayContext;

      const mondayClient2026 = createMondayClient(createMondaySeamlessRequester('2026-01'));
      const newColumnResult = await createConnectBoardColumn(mondayClient2026, targetBoardId, 'Clients', mContext.boardId);

      enqueueSnackbar(`Created connect boards column with the name "${newColumnResult.title}"`, { variant: 'success' });

      onChange(newColumnResult.id);
    } catch (err) {
      enqueueSnackbar(extractErrorMessage(err), { variant: 'error' });
    }
  });

  const columnOptions = board ? board.columns?.filter((col) => col.type === 'board_relation') : [];

  return (
    <Box padding="large" backgroundColor={isCompleted ? 'primaryBackgroundColor' : 'greyBackgroundColor'}>
      <Flex
        direction="row"
        justify="start"
        align="center"
        gap={20}
        style={{
          width: '100%',
        }}
      >
        {errorMessage ? (
          <div style={{ transform: 'translate3d(0,2px,0)' }}>
            <Icon icon={Warning} size={24} />
          </div>
        ) : isCompleted ? (
          <div style={{ transform: 'translate3d(0,2px,0)' }}>
            <Icon icon={Completed} size={24} />
          </div>
        ) : (
          <div style={{ transform: 'scaleX(-1) rotate(90deg) translate3d(-3px,2px,0)' }}>
            <Icon icon={ConvertToSubitem} size={24} />
          </div>
        )}
        <Flex
          direction="column"
          justify="start"
          align="start"
          gap={2}
          style={{
            width: 'inherit',
            maxWidth: 'calc(100% - 330px)' /* 230px */,
          }}
        >
          <Flex gap={8} style={{ maxWidth: '100%' }}>
            <Text
              type="text1"
              align="start"
              color="primary"
              weight="bold"
              style={{
                maxWidth: '100%',
              }}
            >
              {isCompleted ? headline : introText}
            </Text>
            {isCompleted ? (
              <Tooltip content={helpText} position="right" showOnDialogEnter={false}>
                <Icon icon={LearnMore} size={16} style={{ transform: 'translate3d(0,2px,0)' }} />
              </Tooltip>
            ) : null}
          </Flex>
          <Text
            type="text2"
            align="start"
            color="secondary"
            weight="normal"
            style={{
              maxWidth: '100%',
            }}
          >
            {isCompleted ? value : helpText}
          </Text>
        </Flex>

        <Flex gap={16} style={{ marginLeft: 'auto' }}>
          <div style={{ width: '209px' }}>
            {' '}
            {/* '107px' */}
            <ColumnPicker
              placeholder="Select"
              value={value}
              isDisabled={!board}
              columns={columnOptions}
              hideIcons
              onChange={(column) => {
                if (column) {
                  onChange(column.id);
                }
              }}
            />
          </div>

          <Tooltip content="Create a new column on the chosen board and select it" position="left" showOnDialogEnter={false}>
            <IconButton
              icon={AddSmall}
              kind="secondary"
              size="medium"
              disabled={!board || isAddingConnectBoardColumn}
              //loading={isAddingConnectBoardColumn}
              onClick={() => {
                triggerAddConnectBoardColumn(board!.id);
              }}
            />
          </Tooltip>
        </Flex>
      </Flex>
      {errorMessage ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '16px',
            marginTop: '16px',
            padding: '10px 16px',
            border: '1px solid #ecb7bf',
            borderRadius: '4px',
            backgroundColor: '#f4c3cb',
          }}
        >
          <Text type="text3" weight="normal" align="center" color="primary" style={{ flex: 1 }}>
            {errorMessage}
          </Text>
        </div>
      ) : null}
    </Box>
  );
}

type SharedClientBoardSourceProps = {
  source: Partial<SharedClientBoardSourceType>;
  onChange: (source: Partial<SharedClientBoardSourceType>) => void;
  mode: 'create' | 'edit';
  slug?: string | undefined;
};

export function SharedClientBoardSource({ source, onChange }: SharedClientBoardSourceProps) {
  const singleBoardResult = useSingleBoard(source.boardId);
  const { boardName, boardErrorMessage } = extractBoardNameAndErrorMessage(singleBoardResult, source.boardId);
  const { board, mutate } = singleBoardResult;
  const connectBoardColumnErrorMessage = extractConnectBoardColumnErrorMessage(singleBoardResult, source.clientColumnId);

  return (
    <Box
      rounded="small"
      border
      borderColor="uiBorderColor"
      style={{
        margin: '0 0 24px 0',
      }}
    >
      <Box>
        <EditBoardField
          isCompleted={!!source.boardId}
          headline="Board"
          helpText="Each item on this board belongs to a specific client."
          introText="Choose the board with items for multiple clients"
          value={boardName}
          errorMessage={boardErrorMessage}
          onChange={(newSelectedBoardId) => {
            onChange({ ...source, boardId: newSelectedBoardId, clientColumnId: undefined });
          }}
        />

        <Divider withoutMargin />

        <ConnectBoardColumnField
          board={board}
          headline="Connect boards column"
          introText="Select or create the client connection column"
          helpText="This links each item to the client who should see it."
          value={source.clientColumnId}
          isCompleted={!!source.clientColumnId}
          errorMessage={connectBoardColumnErrorMessage}
          onChange={(newColumnId) => {
            onChange({ ...source, clientColumnId: newColumnId });
            mutate();
          }}
        />

        <Divider withoutMargin />

        <EditBoardSourceNameField
          headline="Name"
          helpText="This helps you identify the setup across the app."
          introText="Give this setup a unique name"
          value={source.name || ''}
          isCompleted={!!source.name}
          boardSourceName={source.name || ''}
          onChange={(newBoardSourceName) => {
            onChange({ ...source, name: newBoardSourceName });
          }}
        />
      </Box>
    </Box>
  );
}

export function SharedClientBoardSourceExplainer() {
  return (
    <BoardSourceExplainer
      title="Clients see only their own items in the client portal"
      description="Each client will see only the items from this board that belong to them."
      videoSrc="/Onboarding-Filtered-Board-Compressed.mp4"
    />
  );
}

export function SharedClientBoardSourceIndicator({
  children,
  helpLink,
  hideHelpLink,
}: {
  children: React.ReactNode;
  helpLink?: string;
  hideHelpLink?: boolean;
}) {
  return (
    <Flex gap={2}>
      <Flex gap={2}>
        <Icon icon={Item} size={18} />
        <Icon icon={MoveArrowRight} size={12} />
        <Icon icon={Person} size={16} />
      </Flex>
      <div style={{ marginRight: '8px', marginLeft: '8px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {children}
      </div>
      {helpLink && !hideHelpLink ? (
        <div>
          <ExternalLink href={helpLink} style={{ color: '#1f76c2' }}>
            Need help?
          </ExternalLink>
        </div>
      ) : null}
    </Flex>
  );
}
